import React, {useState} from "react";
import {Link, useLocation, useParams} from "react-router-dom";
import {useLocalStorage} from 'react-use';
import PropTypes from "prop-types";

// react-bootstrap components
import {
  Collapse,
  Nav,
} from "react-bootstrap";

function Sidebar({routes, image, background, layout}) {

  const {employeeId} = useParams();
  const [auth] = useLocalStorage('authenticated');

  // to check for active links and opened collapses
  let location = useLocation();

  // this is for the user collapse
  const [roleCollapseState, setRoleCollapseState] = useState(false);

  // this is for the rest of the collapses
  const [state, setState] = React.useState({});

  React.useEffect(() => {
    setState(getCollapseStates(routes));
  }, []);

  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes prop
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname === routes[i].layout + routes[i].path) {
        return true;
      }
    }
    return false;
  };

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = (routes) => {

    return routes.map((prop, key) => {

      if (prop.redirect) {
        return null;
      }

      if (prop.collapse) {

        var st = {};
        st[prop["state"]] = !state[prop.state];
        return (
          <Nav.Item
            className={getCollapseInitialState(prop.views) ? "active" : ""}
            as="li"
            key={key}
          >
            <Nav.Link
              className={state[prop.state] ? "collapsed" : ""}
              data-toggle="collapse"
              onClick={(e) => {
                e.preventDefault();
                setState({...state, ...st});
              }}
              aria-expanded={state[prop.state]}
            >
              <i className={prop.icon}></i>
              <p>
                {prop.name} <b className="caret"></b>
              </p>
            </Nav.Link>
            <Collapse in={state[prop.state]}>
              <div>
                <Nav as="ul">{createLinks(prop.views)}</Nav>
              </div>
            </Collapse>
          </Nav.Item>
        );
      }

      const employeeIdParam = employeeId ? `/${employeeId}` : ``;
      const path = [prop.layout, employeeIdParam, prop.path].join('');

      return (
        <Nav.Item
          className={activeRoute(path)}
          key={key}
          as="li"
        >
          <Nav.Link to={path} as={Link}>
            {prop.icon ? (
              <>
                <i className={prop.icon} />
                <p>{prop.name}</p>
              </>
            ) : (
              <>
                <span className="sidebar-mini">{prop.mini}</span>
                <span className="sidebar-normal">{prop.name}</span>
              </>
            )}
          </Nav.Link>
        </Nav.Item>
      );
    });
  };

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  const getCurrentRouteRole = () => {

    const roles = JSON.parse(localStorage.getItem('authenticated')).roles;
    return roles.find(role => role.ROLE_CODE === layout)?.ROLE_NAME;
  }


  const renderRouteRole = () => {

    const roles = JSON.parse(localStorage.getItem('authenticated')).roles
      .filter(role => role.ROLE_CODE !== layout);

    return roles && roles.map((role, key) => {
      const employeeIdRoute = role.ROLE_CODE === 'manager' || role.ROLE_CODE === 'admin' ? `` : `/${auth.employee.EMP_ID}`;
      return (
        <Nav.Item
          key={key}
          as="li"
        >
          <Nav.Link to={`/${role.ROLE_CODE}${employeeIdRoute}/dashboard`} as={Link}>
            <span className="sidebar-mini"></span>
            <span className="sidebar-normal">{role.ROLE_NAME}</span>
          </Nav.Link>
        </Nav.Item>
      )
    });
  }


  return (
    <>
      <div className="sidebar" data-color={background} data-image={image}>
        <div className="sidebar-wrapper">
          <div className="logo">
            <a
              className="simple-text logo-mini"
              href="https://umn.ac.id"
            >
              <div className="logo-img">
                <img
                  src={require("assets/img/umn-logo.png").default}
                  alt="react-logo"
                />
              </div>
            </a>
            <a
              className="simple-text logo-normal"
              href="/"
            >
              SIAMI
            </a>
          </div>
          <div className="user">
            <div className="info">
              <a
                data-toggle="collapse"
                href="#pablo"
                onClick={e => e.preventDefault()}
              >
                <span>
                  {JSON.parse(localStorage.getItem('authenticated')).employee.EMP_NAME}
                </span>
              </a>
            </div>
          </div>
          <div className="user">
            <div className="info">
              <a
                className={roleCollapseState ? "collapsed" : ""}
                data-toggle="collapse"
                href="#pablo"
                onClick={(e) => {
                  e.preventDefault();
                  setRoleCollapseState(!roleCollapseState);
                }}
                aria-expanded={roleCollapseState}
              >
                <span>
                  {getCurrentRouteRole()} <b className="caret"></b>
                </span>
              </a>
              <Collapse id="collapseExample" in={roleCollapseState}>
                <div>
                  <Nav as="ul">
                    {renderRouteRole()}
                  </Nav>
                </div>
              </Collapse>
            </div>
          </div>
          <Nav as="ul">{createLinks(routes)}</Nav>
        </div>
        <div
          className="sidebar-background"
          style={{
            backgroundImage: "url('" + image + "')",
          }}
        ></div>
      </div>
    </>
  );
}

let linkPropTypes = {
  path: PropTypes.string,
  layout: PropTypes.string,
  name: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
};

Sidebar.defaultProps = {
  image: "",
  background: "black",
  routes: [],
};

Sidebar.propTypes = {
  image: PropTypes.string,
  background: PropTypes.oneOf([
    "black",
    "azure",
    "green",
    "orange",
    "red",
    "purple",
  ]),
  routes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        ...linkPropTypes,
        icon: PropTypes.string,
      }),
      PropTypes.shape({
        collapse: true,
        path: PropTypes.string,
        name: PropTypes.string,
        state: PropTypes.string,
        icon: PropTypes.string,
        views: PropTypes.shape({
          ...linkPropTypes,
          mini: PropTypes.string,
        }),
      }),
    ])
  ),
};

export default Sidebar;
